import React, { useState, useMemo } from 'react';
import { Table, Badge } from 'react-bootstrap';

export default function JournalTablePaginationView(props) {
  const { searchResult, handleShow } = props;
  const [sortField, setSortField] = useState('journalTitle');
  const [sortOrder, setSortOrder] = useState('asc');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!searchResult || searchResult.length === 0) return [];
    return [...searchResult].sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';

      if (sortField === 'journalID') {
        const numA = parseInt(valA, 10);
        const numB = parseInt(valB, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortOrder === 'asc' ? numA - numB : numB - numA;
        }
      }

      const strA = valA.toString().toLowerCase();
      const strB = valB.toString().toLowerCase();
      if (strA < strB) return sortOrder === 'asc' ? -1 : 1;
      if (strA > strB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [searchResult, sortField, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));

  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(sortedData.length, startIndex + pageSize);
  const paginatedResults = sortedData.slice(startIndex, endIndex);

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted ms-1 small" style={{ opacity: 0.4 }}></i>;
    }
    return sortOrder === 'asc' ? (
      <i className="bi bi-arrow-up text-primary ms-1"></i>
    ) : (
      <i className="bi bi-arrow-down text-primary ms-1"></i>
    );
  };

  const renderStatusBadge = (status) => {
    if (status === 'Active' || status === 'Available') {
      return <Badge bg="success" className="px-2 py-1">Available</Badge>;
    }
    if (status === 'Issued') {
      return <Badge bg="warning" text="dark" className="px-2 py-1">Issued</Badge>;
    }
    return <Badge bg="secondary" className="px-2 py-1">{status || 'Available'}</Badge>;
  };

  if (!searchResult || searchResult.length === 0) return null;

  return (
    <div className="card shadow-sm border-0 rounded-4 overflow-hidden bg-white mb-4">
      {/* Top Toolbar */}
      <div className="d-flex flex-wrap justify-content-between align-items-center p-3 bg-light border-bottom gap-2">
        <div className="d-flex align-items-center gap-2">
          <label className="small fw-semibold text-muted mb-0">Show</label>
          <select
            className="form-select form-select-sm"
            style={{ width: '80px' }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="small text-muted">entries per page</span>
        </div>

        <div className="small text-muted">
          Showing <strong>{startIndex + 1}</strong> - <strong>{endIndex}</strong> of <strong>{sortedData.length}</strong> periodicals
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <Table hover responsive className="mb-0 align-middle">
          <thead style={{ background: '#f1f5f9', color: '#063547', fontSize: '0.86rem', fontWeight: 700 }}>
            <tr>
              <th style={{ width: '50px' }} className="text-center">#</th>
              <th style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleSort('journalID')}>
                Journal ID {renderSortIcon('journalID')}
              </th>
              <th style={{ minWidth: '220px', cursor: 'pointer' }} onClick={() => handleSort('journalTitle')}>
                Journal Title {renderSortIcon('journalTitle')}
              </th>
              <th style={{ minWidth: '140px', cursor: 'pointer' }} onClick={() => handleSort('journalSubject')}>
                Subject {renderSortIcon('journalSubject')}
              </th>
              <th style={{ minWidth: '110px', cursor: 'pointer' }} onClick={() => handleSort('frequency')}>
                Frequency {renderSortIcon('frequency')}
              </th>
              <th style={{ minWidth: '110px', cursor: 'pointer' }} onClick={() => handleSort('liblocation')}>
                Location {renderSortIcon('liblocation')}
              </th>
              <th style={{ minWidth: '90px' }} className="text-center">Status</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: '0.88rem' }}>
            {paginatedResults.map((item, index) => {
              const isBoundJournal = (item.journalType || '').trim().toLowerCase() === 'bound journal';

              return (
                <tr
                  key={item.journalID || startIndex + index}
                  onClick={(event) => handleShow(event, item)}
                  style={{ cursor: 'pointer' }}
                  className="vj-table-row"
                >
                  <td className="text-center fw-bold text-muted">{startIndex + index + 1}</td>
                  <td>
                    <span className="badge bg-light text-primary border font-monospace px-2 py-1">
                      #{item.journalID}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex align-items-center flex-wrap gap-1">
                      <span className="fw-bold text-dark">{item.journalTitle}</span>
                      {isBoundJournal && (
                        <span className="badge-bound-journal ms-1">
                          <i className="bi bi-journal-bookmark-fill me-1"></i>Bound Journal
                        </span>
                      )}
                    </div>
                    {item.publisher && item.publisher !== 'NA' && (
                      <div className="text-muted small">
                        <i className="bi bi-building me-1"></i>{item.publisher}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="badge bg-light text-dark border">
                      {item.journalSubject || 'General'}
                    </span>
                  </td>
                  <td>
                    <span className="text-muted small">
                      <i className="bi bi-calendar3 me-1"></i>{item.frequency || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className="small text-secondary font-monospace">
                      <i className="bi bi-geo-alt me-1 text-danger"></i>{item.liblocation || 'Periodicals Section'}
                    </span>
                  </td>
                  <td className="text-center">
                    {renderStatusBadge(item.journalStatus)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="d-flex flex-wrap justify-content-between align-items-center p-3 bg-light border-top gap-2">
          <div className="small text-muted">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </div>

          <nav aria-label="Journal pagination">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous"
                >
                  &laquo;
                </button>
              </li>
              {getPageNumbers().map((p) => (
                <li key={p} className={`page-item ${p === currentPage ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(p)}>
                    {p}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next"
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}
